// Elementy UI (@theme/ui) — kontrakt znaczników: docs/ARCHITEKTURA.md §5.4.
import { reducedMotion, strings } from '@theme/core';

// Poniżej `data-below` jedna pozycja naraz; zmiany na wspólnym zegarze strony przesuniętym o `data-phase` (NT-01).
class TextRotator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-rotator-item]')];
    if (this.items.length < 2) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.wide = this.dataset.below ? matchMedia(`(min-width: ${this.dataset.below}px)`) : null;
    this.wide?.addEventListener('change', () => this.start(), { signal });
    this.addEventListener('shopify:block:select', (event) => this.select(event.target), { signal });
    this.addEventListener('shopify:block:deselect', () => this.select(null), { signal });
    this.start();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.timer);
  }

  start() {
    clearTimeout(this.timer);
    this.index = Number(this.dataset.start || 0) % this.items.length;
    this.render();
    if (!this.wide?.matches) this.schedule();
  }

  schedule() {
    const interval = Number(this.dataset.interval) || 4000;
    const delay = ((((Number(this.dataset.phase) || 0) * interval - performance.now()) % interval) + interval) % interval;
    this.timer = setTimeout(() => this.tick(), delay < 50 ? delay + interval : delay);
  }

  tick() {
    // W tle, przy zaznaczonym bloku w edytorze i z fokusem w środku pozycja zostaje.
    if (!document.hidden && !this.selected && !this.matches(':focus-within')) {
      this.index = (this.index + 1) % this.items.length;
      this.render();
    }
    this.schedule();
  }

  select(target) {
    const index = target ? this.items.findIndex((item) => item.contains(target)) : -1;
    this.selected = index >= 0;
    if (this.selected) this.index = index;
    this.render();
  }

  render() {
    this.items.forEach((item, index) => {
      const hidden = !this.wide?.matches && index !== this.index;
      item.classList.toggle('is-active', index === this.index);
      item.inert = hidden;
      hidden ? item.setAttribute('aria-hidden', 'true') : item.removeAttribute('aria-hidden');
    });
  }
}

// Zwinięta do `data-collapsed` px albo `data-lines` linii; przycisk tylko przy nadmiarze > `data-threshold`.
class ReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    if (!this.content || !this.toggle) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.phone = this.hasAttribute('data-mobile-only') ? matchMedia('(max-width: 749px)') : null;
    this.phone?.addEventListener('change', () => this.expand(false), { signal });
    this.toggle.addEventListener('click', () => this.flip(), { signal });
    this.measure();
    // W następnej klatce: zmiana max-height w obserwatorze dałaby błąd „ResizeObserver loop”.
    this.observer = new ResizeObserver(() => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.measure());
    });
    this.observer.observe(this.content);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.animation?.cancel();
    cancelAnimationFrame(this.frame);
  }

  measure() {
    if (this.classList.contains('is-expanded')) return;
    const { content, dataset } = this;
    let collapsible = false;
    if (!this.phone || this.phone.matches) {
      const lines = Number(dataset.lines);
      const lineHeight = parseFloat(getComputedStyle(content.querySelector('p') || content).lineHeight) || 24;
      this.height = lines ? Math.round(lineHeight * lines + parseFloat(getComputedStyle(content).paddingTop)) : Number(dataset.collapsed) || 320;
      collapsible = content.scrollHeight > this.height + Number(dataset.threshold ?? (lines ? lineHeight / 2 : 0));
    }
    this.classList.toggle('is-collapsible', collapsible);
    content.style.maxHeight = collapsible ? `${this.height}px` : '';
    content.style.overflow = collapsible ? 'hidden' : '';
    this.toggle.hidden = !collapsible;
  }

  expand(expanded) {
    this.classList.toggle('is-expanded', expanded);
    this.toggle.setAttribute('aria-expanded', expanded);
    const { more, less } = strings(this);
    const label = this.toggle.querySelector('[data-read-more-label]');
    if (label && more) label.textContent = expanded ? less : more;
    if (expanded) this.content.style.maxHeight = '';
    else this.measure();
  }

  flip() {
    const expanded = !this.classList.contains('is-expanded');
    const start = this.content.offsetHeight;
    this.animation?.cancel();
    this.expand(expanded);
    const duration = Number(this.dataset.duration ?? 320);
    // max-height (nie height): animacja nadpisuje inline max-height zwiniętej treści.
    this.animation = duration && !reducedMotion()
      ? this.content.animate({ maxHeight: [`${start}px`, `${this.content.offsetHeight}px`] }, { duration, easing: 'ease-in-out' })
      : null;
    if (expanded) return;
    // Po zwinięciu czytelnik nie może zostać daleko pod treścią; offset pod nagłówkiem (scroll-margin-top).
    const offset = this.dataset.scrollOffset;
    if (offset === undefined) {
      const reveal = () => this.toggle.scrollIntoView({ block: 'nearest' });
      return this.animation ? this.animation.finished.then(reveal, () => {}) : reveal();
    }
    const top = this.getBoundingClientRect().top - parseFloat(getComputedStyle(this.toggle).scrollMarginTop);
    if (top < 0) scrollBy({ top: top - Number(offset), behavior: reducedMotion() ? 'auto' : 'smooth' });
  }
}

// Strzałka widoczna tylko przy przepełnieniu i gdy jest dokąd przewinąć (tolerancja 2 px).
class ScrollCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]') || this;
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.prev?.addEventListener('click', () => this.go(-1), { signal });
    this.next?.addEventListener('click', () => this.go(1), { signal });
    this.track.addEventListener('scroll', () => (this.frame ||= requestAnimationFrame(() => this.update())), { passive: true, signal });
    this.addEventListener('shopify:block:select', ({ target }) => {
      if (this.track.contains(target)) target.scrollIntoView({ block: 'nearest', inline: 'center' });
    }, { signal });
    // Tab: Chrome nie przewija kafla, który mieści się w torze, ale leży pod przyklejonym elementem
    // (np. „Wróć do”); scrollIntoView uwzględnia scroll-padding toru. Tylko klawiatura — klik nie przesuwa celu.
    this.track.addEventListener('focusin', ({ target }) => {
      if (target.matches(':focus-visible')) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, { signal });
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.track);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  go(direction) {
    const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    const left = direction * rtl * this.track.clientWidth * (Number(this.dataset.step) || 0.8);
    this.track.scrollBy({ left, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  update() {
    this.frame = 0;
    const { scrollWidth, clientWidth, scrollLeft } = this.track;
    const max = scrollWidth - clientWidth;
    const position = Math.abs(scrollLeft); // w RTL ujemny
    const overflowing = max > 2;
    this.classList.toggle('is-overflowing', overflowing);
    this.classList.toggle('is-at-start', position <= 2);
    this.classList.toggle('is-at-end', position >= max - 2);
    this.arrow(this.prev, overflowing && position > 2, this.next);
    this.arrow(this.next, overflowing && position < max - 2, this.prev);
  }

  arrow(button, enabled, other) {
    if (!button || button.hidden === !enabled) return;
    if (!enabled && document.activeElement === button && other && !other.hidden) other.focus(); // fokus nie przepada
    button.hidden = button.disabled = !enabled;
  }
}

for (const [name, element] of [['text-rotator', TextRotator], ['read-more', ReadMore], ['scroll-carousel', ScrollCarousel]]) {
  if (!customElements.get(name)) customElements.define(name, element);
}
